import * as React from "react"
import { cn } from "@/lib/utils"

export interface KbdProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * The keyboard key to display
   */
  keys?: string[]
}

const Kbd = React.forwardRef<HTMLElement, KbdProps>(
  ({ className, keys, children, ...props }, ref) => {
    const isMac = typeof window !== 'undefined' && /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)
    
    const displayKeys = keys?.map(key => {
      // Convert common keys to symbols on Mac
      if (isMac) {
        switch (key.toLowerCase()) {
          case 'cmd':
          case 'command':
            return '⌘'
          case 'ctrl':
          case 'control':
            return '⌃'
          case 'opt':
          case 'option':
          case 'alt':
            return '⌥'
          case 'shift':
            return '⇧'
          case 'enter':
          case 'return':
            return '↵'
          case 'delete':
          case 'backspace':
            return '⌫'
          case 'esc':
          case 'escape':
            return '⎋'
          case 'tab':
            return '⇥'
          case 'up':
            return '↑'
          case 'down':
            return '↓'
          case 'left':
            return '←'
          case 'right':
            return '→'
          default:
            return key
        }
      } else {
        // Windows/Linux - use text
        switch (key.toLowerCase()) {
          case 'cmd':
          case 'command':
            return 'Ctrl'
          case 'opt':
          case 'option':
            return 'Alt'
          default:
            return key.charAt(0).toUpperCase() + key.slice(1)
        }
      }
    })

    return (
      <kbd
        ref={ref}
        className={cn(
          "pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100",
          className
        )}
        {...props}
      >
        {displayKeys ? displayKeys.map((key, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="text-xs">+</span>}
            <span>{key}</span>
          </React.Fragment>
        )) : children}
      </kbd>
    )
  }
)
Kbd.displayName = "Kbd"

export { Kbd }
